// Revela o texto de uma resposta em streaming num ritmo constante.
//
// Por que existe: modelos rápidos (ex.: gpt-5-nano) e proxies que juntam pacotes fazem os
// deltas chegarem em rajada, e a resposta "pula" inteira na tela. Aqui os deltas entram numa
// fila e são exibidos aos poucos; se a fila cresce, a velocidade aumenta para nunca ficar
// mais de `maxLagMs` atrás do que já chegou (a resposta não demora mais que o necessário).

const TICK_MS = 24;

export function createSmoothWriter(apply, { minCharsPerSecond = 90, maxLagMs = 1200 } = {}) {
  let queue = '';
  let timer = null;
  let last = 0;
  let waiters = [];

  const settleIfEmpty = () => {
    if (queue) return;
    const pending = waiters;
    waiters = [];
    pending.forEach((resolve) => resolve());
  };

  const schedule = () => {
    if (timer !== null) return;
    last = performance.now();
    timer = setTimeout(tick, TICK_MS);
  };

  const flush = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (queue) {
      const rest = queue;
      queue = '';
      apply(rest);
    }
    settleIfEmpty();
  };

  function tick() {
    timer = null;
    // Aba em segundo plano: os navegadores seguram os timers; não faz sentido animar.
    if (typeof document !== 'undefined' && document.hidden) {
      flush();
      return;
    }
    const now = performance.now();
    const dt = Math.min(now - last, 100);
    const charsPerSecond = Math.max(minCharsPerSecond, queue.length / (maxLagMs / 1000));
    let count = Math.max(1, Math.round((charsPerSecond * dt) / 1000));
    // Não corta um par substituto (emoji) ao meio.
    const code = queue.charCodeAt(count - 1);
    if (code >= 0xd800 && code <= 0xdbff) count += 1;

    const chunk = queue.slice(0, count);
    queue = queue.slice(count);
    apply(chunk);

    if (queue) schedule();
    else settleIfEmpty();
  }

  return {
    push(text) {
      if (!text) return;
      queue += text;
      schedule();
    },
    /** Exibe tudo o que falta imediatamente (erro, cancelamento, fim de conexão). */
    flush,
    /** Resolve quando a fila esvaziou pelo ritmo normal. */
    drain() {
      if (!queue) return Promise.resolve();
      return new Promise((resolve) => waiters.push(resolve));
    },
  };
}
